import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { registrationApi } from "../api/registration";
import { Field, ProblemAlert } from "../components/ui";

/** Opened from the verification email (?token=…). Without a token, or when the link has failed, it offers a new link. */
export function VerifyEmailPage() {
  const [params, setParams] = useSearchParams();
  // Read once, then dropped from the address bar so the live token doesn't stay in browser history.
  const [token, setLinkToken] = useState(() => params.get("token"));
  useEffect(() => {
    const fromLink = params.get("token");
    if (fromLink) {
      setLinkToken(fromLink);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  const verify = useMutation({ mutationFn: registrationApi.verify });
  const { mutate } = verify;
  useEffect(() => {
    if (token) mutate(token);
  }, [token, mutate]);

  return (
    <main className="login">
      <section className="login__card" aria-labelledby="verify-title">
        <p className="brand">RegiGov</p>
        <h1 id="verify-title">Verify your email address</h1>
        {token && verify.isPending && (
          <p className="muted">Checking your link…</p>
        )}
        {verify.isSuccess ? (
          <>
            <p role="status" className="notice">
              Your email address is verified. You can now sign in.
            </p>
            <p>
              <Link to="/login">Sign in</Link>
            </p>
          </>
        ) : (
          <>
            <ProblemAlert error={verify.error} />
            {(!token || verify.isError) && <ResendForm />}
          </>
        )}
      </section>
    </main>
  );
}

function ResendForm() {
  const [email, setEmail] = useState("");
  const resend = useMutation({ mutationFn: registrationApi.resend });
  function onSubmit(e: FormEvent) {
    e.preventDefault();
    resend.mutate(email);
  }
  if (resend.data) {
    return (
      <>
        <p role="status" className="notice">
          {resend.data.message}
        </p>
        <p>
          <Link to="/login">Back to sign in</Link>
        </p>
      </>
    );
  }
  return (
    <form onSubmit={onSubmit} className="form">
      <p className="muted">
        Enter your email address and we will send you a new verification link.
        Earlier links stop working.
      </p>
      <Field label="Email address">
        <input
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={resend.isPending}
        />
      </Field>
      <ProblemAlert error={resend.error} />
      <button type="submit" disabled={resend.isPending}>
        {resend.isPending ? "Sending…" : "Send a new link"}
      </button>
      <p>
        <Link to="/login">Back to sign in</Link>
      </p>
    </form>
  );
}
